/**
 * @file legal-doc-view.ts
 * @description Подписи и реестры вида юридических документов в кабинете
 * (phase19.16, SB139–SB141; uiux-legal-docs §2, uiux-legal-steps-b-c §6.4).
 *
 * Реестры, а не ветки: значок и подпись вида (`LEGAL_KIND_META`), тон
 * пилюли по состоянию (`LEGAL_STATUS_TONE`). Новый вид документа = запись
 * в реестре и в `LEGAL_DOCUMENT_KINDS` shared. Подписи состояния — функция
 * shared `legalDocumentStatusLabel` (те же правила, что у API).
 */

import {
  formatLegalFileSize,
  LEGAL_DOCUMENT_KIND_HINTS,
  LEGAL_DOCUMENT_KIND_LABELS,
  LEGAL_WARNING_MESSAGES,
  legalDocumentStatusLabel,
  type LegalDocumentKind,
  type LegalDocumentListItemDto,
  type LegalDocumentStatus,
  type LegalDocumentWarning,
  type LegalListStatusFilter,
} from "@chaiberi/shared";
import { AlignLeft, FileText, FolderOpen, type LucideIcon } from "lucide-react";

import type { ShopStatusMeta, StatusTone } from "../shop/status-meta";
import { expiredCountText } from "./legal-certificates";

/** Значок, подпись и пояснение вида — для списка и окна «Новый документ». */
export interface LegalKindMeta {
  icon: LucideIcon;
  label: string;
  hint: string;
}

export const LEGAL_KIND_META: Readonly<Record<LegalDocumentKind, LegalKindMeta>> = {
  TEXT: { icon: AlignLeft, label: LEGAL_DOCUMENT_KIND_LABELS.TEXT, hint: LEGAL_DOCUMENT_KIND_HINTS.TEXT },
  PDF: { icon: FileText, label: LEGAL_DOCUMENT_KIND_LABELS.PDF, hint: LEGAL_DOCUMENT_KIND_HINTS.PDF },
  GROUP: { icon: FolderOpen, label: LEGAL_DOCUMENT_KIND_LABELS.GROUP, hint: LEGAL_DOCUMENT_KIND_HINTS.GROUP },
};

/** Четвёртая карточка «Новый документ» — автоматическая группа (uiux-legal-steps-b-c §6.4). */
export const LEGAL_CERTIFICATES_KIND_META: LegalKindMeta = {
  icon: FolderOpen,
  label: "Сертификаты производителей",
  hint: "Список собирается сам из карточек производителей",
};

/** Тон пилюли по состоянию документа (db-design §1.3). */
export const LEGAL_STATUS_TONE: Readonly<Record<LegalDocumentStatus, StatusTone>> = {
  draft: "neutral",
  published: "success",
  changed: "attention",
  unpublished: "neutral",
  empty: "attention",
};

/** Поля, по которым строится пилюля (строка списка и карточка). */
export interface LegalStatusSource {
  kind: LegalDocumentKind;
  status: LegalDocumentStatus;
  live: { number: number } | null;
  /** Видимых документов группы; у карточки — из `members`. */
  visibleMembers?: number;
}

/** Пилюля состояния: «Опубликован · ред. 4», «Снят (ред. 2 сохранена)». */
export function legalStatusMeta(doc: LegalStatusSource): ShopStatusMeta {
  return {
    tone: LEGAL_STATUS_TONE[doc.status],
    label: legalDocumentStatusLabel(doc.status, {
      kind: doc.kind,
      liveNumber: doc.live?.number ?? null,
      visibleMembers: doc.visibleMembers,
    }),
  };
}

/** Строка вида в списке: «PDF-файл · 2,4 МБ», «Группа документов · 3 док.», «… · автоматически · 5 на сайте». */
export function legalKindLine(item: LegalDocumentListItemDto): string {
  const parts = [LEGAL_KIND_META[item.kind].label];
  if (item.kind === "PDF" && item.file) parts.push(formatLegalFileSize(item.file.sizeBytes));
  if (item.kind === "GROUP") {
    if (item.groupSource === "PRODUCER_CERTIFICATES") {
      parts.push("автоматически");
      if (item.members) parts.push(`${item.members.visible} на сайте`);
      // Просроченные — из предупреждения списка (`legal_certificates_expired`).
      const expired = item.warnings.find((w) => w.code === "legal_certificates_expired")?.count ?? 0;
      if (expired > 0) parts.push(expiredCountText(expired));
    } else if (item.members) {
      parts.push(`${item.members.total} док.`);
    }
  }
  return parts.join(" · ");
}

/** Текст предупреждения: у просроченных сертификатов — с числом. */
export function legalWarningText(warning: LegalDocumentWarning): string {
  if (warning.code === "legal_certificates_expired" && warning.count) {
    return `Просроченных сертификатов: ${warning.count} — на сайте они не показываются.`;
  }
  if (warning.code === "legal_member_not_published" && warning.count) {
    return `Не опубликовано в группе: ${warning.count} — на сайте их не видно.`;
  }
  if (warning.code === "legal_group_becomes_empty" && warning.group) {
    return `Группа «${warning.group.title}» станет пустой и пропадёт с сайта.`;
  }
  return LEGAL_WARNING_MESSAGES[warning.code];
}

/** Отбор списка по состоянию: «Опубликованные» — показываемые; «Черновики» — черновики и снятые с публикации. */
const STATUS_FILTER: Readonly<Record<LegalListStatusFilter, (s: LegalDocumentStatus) => boolean>> = {
  all: () => true,
  published: (s) => s === "published" || s === "changed" || s === "empty",
  draft: (s) => s === "draft" || s === "unpublished",
};

/**
 * Отбор на клиенте — повтор отбора API: прежний API параметры `status`/`kind`
 * не знает и отдаёт всё (терпимое чтение до выкладки).
 */
export function filterLegalItems(
  items: readonly LegalDocumentListItemDto[],
  filter: { status: LegalListStatusFilter; kind: LegalDocumentKind | null },
): LegalDocumentListItemDto[] {
  return items.filter((item) => STATUS_FILTER[filter.status](item.status) && (filter.kind === null || item.kind === filter.kind));
}

/** Автоматическая группа сертификатов магазина (одна), если уже создана. */
export function findCertificatesGroup(items: readonly LegalDocumentListItemDto[]): LegalDocumentListItemDto | null {
  return items.find((item) => item.kind === "GROUP" && item.groupSource === "PRODUCER_CERTIFICATES") ?? null;
}

/** Дата по-русски: «12.10.2026». */
export function formatLegalDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** SB139: текст страницы документов вместо устаревшего «раздел /legal/:slug — добавим в phase10.6». */
export const LEGAL_LIST_INTRO =
  "Опубликованный документ сразу доступен на сайте по своему адресу. Чтобы покупатели его нашли, добавьте документ или группу в меню подвала или в блок «Ссылки» подвала. Оферта и политика ПДн показываются в нижней полосе подвала автоматически.";
